import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { cn } from '@crabs-ai/ds-react';

interface CaptionWordProps {
  word: string;
  displayWord: string;
  isActive: boolean;
  absStart?: number;
  itemId: string;
  wordId: string;
  wordIndex: number;
  onWordClick: (startInMs?: number) => void;
  onWordKeyDown: (
    event: React.KeyboardEvent<HTMLParagraphElement>,
    originWord: string,
    itemId: string,
    wordIndex: number,
  ) => void;
  onInput: (
    e: React.InputEvent<HTMLParagraphElement>,
    originWord: string,
    itemId: string,
    wordIndex: number,
  ) => void;
  isEditable?: boolean;
  isLinkingEnabled?: boolean;
}

const CaptionWord = React.memo(function CaptionWord({
  word,
  displayWord,
  isActive,
  absStart,
  itemId,
  wordId,
  wordIndex,
  onWordClick,
  onWordKeyDown,
  onInput,
  isEditable = true,
  isLinkingEnabled = false,
}: CaptionWordProps) {
  const elementRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    if (element.textContent !== displayWord) {
      element.textContent = displayWord;
    }
  }, [displayWord]);

  const handleClick = useCallback(() => {
    onWordClick(absStart);
  }, [absStart, onWordClick]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLParagraphElement>) => {
      onWordKeyDown(event, word, itemId, wordIndex);
    },
    [itemId, onWordKeyDown, word, wordIndex],
  );

  const handleInput: React.InputEventHandler<HTMLParagraphElement> =
    useCallback(
      (event) => {
        onInput(event, word, itemId, wordIndex);
      },
      [itemId, onInput, word, wordIndex],
    );

  // Link 활성화 시: contentEditable=false (글자 단위 편집 차단), 하지만 onKeyDown은 유지 (단어 단위 조작)
  const editableProps = isEditable
    ? isLinkingEnabled
      ? {
          // Link 활성화: 글자 편집 차단, 키보드 단어 조작만 허용
          contentEditable: false as const,
          onKeyDown: handleKeyDown,
          onInput: undefined,
          tabIndex: 0, // 포커스 가능하도록
        }
      : {
          // 일반 모드: 글자 단위 편집 허용
          contentEditable: true as const,
          suppressContentEditableWarning: true as const,
          onKeyDown: handleKeyDown,
          onInput: handleInput,
        }
    : {
        onKeyDown: undefined,
        onInput: undefined,
      };

  return (
    <p
      ref={elementRef}
      className={cn(
        'rounded-3xs p-5xs text-body-medium text-sys-cont-inverse-dark hover:bg-sys-bg-surface-primary',
        isActive ? 'bg-sys-bg-surface-primary' : undefined,
        !isEditable ? 'cursor-pointer' : undefined,
        'min-w-0 max-w-full',
        // Link 활성화 시 단어별 구분 스타일
        isLinkingEnabled &&
          'border-sys-outline-primary-subtle mx-[1px] cursor-pointer rounded-3xs border focus:ring-2 focus:ring-blue-500 focus:outline-none',
      )}
      data-active-word={isActive ? 'true' : undefined}
      data-word-id={wordId}
      data-linking-enabled={isLinkingEnabled ? 'true' : undefined}
      onClick={handleClick}
      style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
      {...editableProps}
    />
  );
});

export default CaptionWord;
